import { computed, ref, shallowRef } from 'vue'
import { SELECT_ALL_MAX } from './useCandidateQuery'

/** Selection state shared by every candidate/membership table (Account, Contact, Engaged). */
export function useCandidateSelection<T extends { membership_id: string }>(opts: {
  items: () => T[]
  fetchAllMatching: () => Promise<T[]>
}) {
  // shallowRef: the Set/Map is always replaced wholesale, never mutated in
  // place, and deep reactivity here just fights TS's Map<string, T> inference.
  const selectedIds = shallowRef<Set<string>>(new Set())
  const selectedRows = shallowRef<Map<string, T>>(new Map())
  const selectingAll = ref(false)
  let lastClickedIndex = -1

  function isSelected(m: T): boolean {
    return selectedIds.value.has(m.membership_id)
  }

  function toggleSelect(m: T, event?: Event) {
    const newIds = new Set(selectedIds.value)
    const newRows = new Map(selectedRows.value)
    const items = opts.items()
    const idx = items.findIndex(x => x.membership_id === m.membership_id)

    if ((event as any)?.shiftKey && lastClickedIndex >= 0 && idx >= 0) {
      const start = Math.min(lastClickedIndex, idx)
      const end = Math.max(lastClickedIndex, idx)
      for (let i = start; i <= end; i++) {
        newIds.add(items[i].membership_id)
        newRows.set(items[i].membership_id, items[i])
      }
    } else if (newIds.has(m.membership_id)) {
      newIds.delete(m.membership_id)
      newRows.delete(m.membership_id)
    } else {
      newIds.add(m.membership_id)
      newRows.set(m.membership_id, m)
    }

    lastClickedIndex = idx
    selectedIds.value = newIds
    selectedRows.value = newRows
  }

  function selectAllLoaded() {
    const newIds = new Set(selectedIds.value)
    const newRows = new Map(selectedRows.value)
    for (const m of opts.items()) {
      newIds.add(m.membership_id)
      newRows.set(m.membership_id, m)
    }
    selectedIds.value = newIds
    selectedRows.value = newRows
  }

  function clearSelection() {
    selectedIds.value = new Set()
    selectedRows.value = new Map()
    lastClickedIndex = -1
  }

  const allLoadedSelected = computed(() => {
    const items = opts.items()
    return items.length > 0 && items.every(m => selectedIds.value.has(m.membership_id))
  })

  function toggleSelectAll() {
    if (allLoadedSelected.value) {
      clearSelection()
    } else {
      selectAllLoaded()
    }
  }

  /** Every row matching the filter, across pages — the toolbar's escalation offer. */
  async function selectAllMatching(onCapped?: (message: string) => void) {
    selectingAll.value = true
    try {
      const rows = await opts.fetchAllMatching()
      const newIds = new Set<string>()
      const newRows = new Map<string, T>()
      for (const r of rows) {
        newIds.add(String(r.membership_id))
        newRows.set(String(r.membership_id), r)
      }
      selectedIds.value = newIds
      selectedRows.value = newRows
      if (onCapped && rows.length >= SELECT_ALL_MAX) {
        onCapped(`Selection capped at ${SELECT_ALL_MAX} candidates.`)
      }
    } finally {
      selectingAll.value = false
    }
  }

  return {
    selectedIds,
    selectedRows,
    selectingAll,
    isSelected,
    toggleSelect,
    selectAllLoaded,
    selectAllMatching,
    clearSelection,
    allLoadedSelected,
    toggleSelectAll,
  }
}
